import { useTranslations } from '@vocab/react';
import { Box, Button, Dropdown, Inline, Stack } from 'braid-design-system';
import { useScreenSize } from 'src/client/hooks/useScreenSize';
import translations from '../../../../client/.vocab';
import translations_tal from '../.vocab';
import { QueryFilterHeading } from './QueryFilterHeading';
import { useFields } from './useFields';
import { useSearchParams } from 'react-router-dom';
import { dataLabHelper } from '../datalabHelper';

interface FilterProp {
  filterLabel?: string;
  filterPlaceholder?: string;
}

interface DriverFilterProp extends FilterProp {
  optionFilter: FilterProp;
}

export interface DriverOption {
  name: string;
}
export interface Driver {
  name: string;
  options: DriverOption[];
}
export interface DriverList {
  drivers: Driver[];
}

interface Props {
  driverList: DriverList;
  handleSubmit: any;
  showDrawerFilter?: boolean;
}

export const QueryFilter = ({
  driverList,
  handleSubmit,
  showDrawerFilter = false,
}: Props) => {
  const { t } = useTranslations(translations);
  const { t: t_tal } = useTranslations(translations_tal);
  const searchParams = useSearchParams()[0];
  // eslint-disable-next-line
  const queryFilterParams = dataLabHelper.getQueryFilterParams(searchParams);

  const primaryDriverList: DriverList = {
    drivers: [{ name: 'ALL', options: [] }, ...driverList.drivers],
  };

  const secondaryDriverList: DriverList = { ...driverList };

  const primaryDriverFilter: DriverFilterProp = {
    filterLabel: t_tal('Expore drivers by'),
    filterPlaceholder: t('Select a filter'),
    optionFilter: {
      filterLabel: t('Select up to two options'),
      filterPlaceholder: t('Select options'),
    },
  };

  const secondaryDriverFilter: DriverFilterProp = {
    filterLabel: t('Filter by'),
    filterPlaceholder: t('Select a filter'),
    optionFilter: {
      filterLabel: t('Select up to two options'),
      filterPlaceholder: t('Select options'),
    },
  };

  const { primaryDriver, secondaryDriver, primaryOptions, secondaryOptions } =
    useFields();

  /*
  To Update the Query Filter Drop Down based on Query Param, but only on first load.
  useEffect(() => {
    primaryDriver.value = queryFilterParams.primaryDriver
    primaryOptions.value = queryFilterParams.primaryDriverOptions ? queryFilterParams.primaryDriverOptions[0] : ""
    secondaryDriver.value = queryFilterParams.secondaryDriver || ""
    secondaryOptions.value = queryFilterParams.secondaryDriverOptions ? queryFilterParams.secondaryDriverOptions[0] : ""
  });
  */

  const onSubmit = handleSubmit((formValues: any) => {
    window.location.href = `?primaryDriver=${formValues.primaryDriver}&primaryDriverOptions=${formValues.primaryOptions}&secondaryDriver=${formValues.secondaryDriver}&secondaryDriverOptions=${formValues.secondaryOptions}`;
  });

  const isMobile = useScreenSize().isMobile;

  return (
    <form onSubmit={onSubmit}>
      <Box style={{ maxWidth: '350px' }}>
        <Stack space="medium">
          {isMobile && !showDrawerFilter && (
            <QueryFilterHeading
              title={t('Filters')}
              description={t_tal(
                'Select filter to uncover deeper insights about candidates',
              )}
            />
          )}
          <DriverFilter
            driver={primaryDriver}
            driverList={primaryDriverList}
            driverOptions={primaryOptions}
            filterProp={primaryDriverFilter}
            primaryDisable={false}
            secondaryDisable={primaryDriver.value === 'ALL'}
          />

          <DriverFilter
            driver={secondaryDriver}
            driverList={secondaryDriverList}
            driverOptions={secondaryOptions}
            filterProp={secondaryDriverFilter}
            primaryDisable={
              primaryDriver.value === 'ALL' || primaryDriver.value === ''
            }
            secondaryDisable={
              primaryDriver.value === 'ALL' || !(secondaryDriver.value !== '')
            }
          />

          <Inline space="small" collapseBelow="tablet">
            <Button variant="solid" type="submit">
              {t('Apply filters')}
            </Button>
            <Button variant="transparent" type="reset">
              {t('Clear all')}
            </Button>
          </Inline>
        </Stack>
      </Box>
    </form>
  );
};

interface FilterFilterProp {
  driver: any;
  driverList: DriverList;
  driverOptions: any;
  filterProp: DriverFilterProp;
  primaryDisable: boolean;
  secondaryDisable: boolean;
}

const DriverFilter = ({
  filterProp,
  driverList,
  primaryDisable,
  secondaryDisable,
  driverOptions,
  driver,
}: FilterFilterProp) => {
  const options = driverList.drivers.find(
    (d) => d.name === driver.value,
  )?.options;
  return (
    <Stack space="medium">
      <Dropdown
        id="PRIMARY_FILTER"
        label={filterProp.filterLabel}
        onChange={driver.onChange}
        placeholder={filterProp.filterPlaceholder}
        value={driver.value}
        disabled={primaryDisable}
        tone={!driver.valid ? 'critical' : 'neutral'}
        message={!driver.valid && driver.errorMessage}
      >
        <>
          {driverList.drivers.map((d) => (
            <option key={d.name} value={d.name}>
              {d.name}
            </option>
          ))}
        </>
      </Dropdown>

      <Dropdown
        id="PRIMARY_OPTION_FILTER"
        label={filterProp.optionFilter.filterLabel}
        onChange={driverOptions.onChange}
        placeholder={filterProp.optionFilter.filterPlaceholder}
        value={driverOptions.value}
        disabled={!driver.value || secondaryDisable}
        tone={!driverOptions.valid ? 'critical' : 'neutral'}
        message={!driverOptions.valid && driverOptions.errorMessage}
      >
        <>
          {options &&
            options.map((option) => (
              <option key={option.name} value={option.name}>
                {option.name}
              </option>
            ))}
        </>
      </Dropdown>
    </Stack>
  );
};
